import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import DataTable from '../../../components/DataTable';
import { textSearch, dataForColumn, intSearch } from '../../../services/tableUtils';
import CustomTablePagination from '../../../components/CustomTablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { Alert, AlertTitle } from '@mui/material'
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import errorMessageToDisplay from '../../../services/error';
import {useGetFundSfdrIndicatorsApi} from '../../../hooks/useApis';
import DateSelector from '../../../components/DateSelector';
import dayjs from 'dayjs';

export const SfdrAutoAssignedIndicators = ({fund}) => {
    const { execute:getFundSfdrIndicators } = useGetFundSfdrIndicatorsApi();

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [sfdrIndicators, setSfdrIndicators] = useState(null);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [useCustomDate, setUseCustomDate] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    const formatDateParam = (date) => {
        if (!date) return null;
        return dayjs(date).format('YYYY-MM-DD');
    };

    const getSfdrIndicatorsList = async (fundCode, getFundSfdrIndicators, dateParam) => {
        setIsLoading(true);
        setErrorMessage('');
        getFundSfdrIndicators(fundCode, dateParam)
        .then((response) => {
          setSfdrIndicators(response ?? []);
          if (response && response.length > 0) {
            setEffectiveDate(response[0].effectiveDate);
          }
          setIsLoading(false);
        })
        .catch((e) => {
          setIsLoading(false);
          setErrorMessage(errorMessageToDisplay);
        })  
      }

    useEffect(() => {
        if(fund && getFundSfdrIndicators) {
            const dateParam = useCustomDate && selectedDate ? formatDateParam(selectedDate) : null;
            getSfdrIndicatorsList(fund.code, getFundSfdrIndicators, dateParam);
        }
        else setSfdrIndicators(null);
    }, [fund, getFundSfdrIndicators]);  

    // Reset the date toggle whenever the selected fund changes
    useEffect(() => {
        setUseCustomDate(false);
        setSelectedDate(null);
    }, [fund]);

    useEffect(() => {
        if (!useCustomDate && fund && getFundSfdrIndicators) {
            getSfdrIndicatorsList(fund.code, getFundSfdrIndicators, null);
        }
    }, [useCustomDate]);

    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date && fund && getFundSfdrIndicators) {
            getSfdrIndicatorsList(fund.code, getFundSfdrIndicators, formatDateParam(date));
        }
    };

    const handleToggleCustomDate = (event) => {
        setUseCustomDate(event.target.checked);
        if (!event.target.checked) {
            setSelectedDate(null);
        }
    };  

    return(
    <>
       {isLoading === true 
        ? 
          <Backdrop open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        : 
          errorMessage
          ? <Alert severity="error">
              <AlertTitle>Error</AlertTitle>  
              {errorMessage}
            </Alert> 
          : 
          <Box sx={{width: "100%"}}>
            {sfdrIndicators !== null ?
            <>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0.5}}>
                <Typography variant="h6">Auto-Assigned Indicators</Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <FormControlLabel 
                    control={<Switch checked={useCustomDate} onChange={handleToggleCustomDate} size="small" />} 
                    label="Select date"
                    sx={{marginLeft: 1}}
                  />
                  {useCustomDate && 
                    <DateSelector 
                      date={selectedDate ? dayjs(selectedDate) : null} 
                      setDate={handleDateChange} 
                      label="Effective Date"
                    />
                  }
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{marginBottom: 1}}>
                {sfdrIndicators.length > 0 && (
                  <>
                    <b>Effective Classification:</b> {sfdrIndicators[0].sfdrArticleClassification}
                    {sfdrIndicators[0].sustainableInvestmentPercentage != null && (
                      <>, <b>SI:</b> {sfdrIndicators[0].sustainableInvestmentPercentage}%</>
                    )}
                    {effectiveDate && !useCustomDate && <> (as at {dayjs(effectiveDate).format('DD MMM YYYY')})</>}
                    <br/>
                  </>
                )}
                Indicators automatically assigned based on the fund's SFDR article classification and Sustainable Investment value in Fundipedia.
              </Typography>
              {sfdrIndicators.length > 0
                ? <SfdrIndicatorsDataTable data={sfdrIndicators}/>
                : useCustomDate && selectedDate
                  ? <Typography variant="body2" color="text.secondary">No indicators assigned for this date.</Typography>
                  : <></>
              }
            </>
            : <></>
            }
          </Box>
      }
    </>
    )
}

const SfdrIndicatorsDataTable = ({ data }) => {
    const navigate = useNavigate();

    const handleRowClick = (event, row) => {
        navigate(`/indicators/${row.esgApplicableIndicatorMasterId}`);
    };

    const [dataCount, setDataCount] = useState(data.length);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(data.length > 5 ? 25 : 5);
    
    const [columnSettings, setColumnSettings] = useState(
    {
        settings:{
            defaultSortColumn: 'indicatorGroupName',
            hasGroup: false
        },
        cells:[
            { id: 'esgApplicableIndicatorMasterId', type: "int", disablePadding: false, label: 'Id', style: { width: "8%" }, filter: {getColumnData: dataForColumn, comparator: intSearch, value:null}, getData: (row) => (row.esgApplicableIndicatorMasterId)},
            { id: 'indicatorGroupName', type: "string", disablePadding: false, label: 'Indicator Group', style: { width: "17%" }, filter: {getColumnData: dataForColumn, comparator: textSearch, value:null}, getData: (row) => (row.indicatorGroupName)},
            { id: 'indicatorName', type: "string", disablePadding: false, label: 'Indicator Name', style: { width: "35%" }, filter: {getColumnData: dataForColumn, comparator: textSearch, value:null}, getData: (row) => (row.indicatorName)},
            { id: 'indicatorType', type: "string", disablePadding: false, label: 'Indicator Type', style: { width: "15%" }, filter: {getColumnData: dataForColumn, comparator: textSearch, value:null}, getData: (row) => (row.indicatorType)},
            { id: 'assignmentReason', type: "string", disablePadding: false, label: 'Assignment Reason', style: { width: "25%" }, filter: {getColumnData: dataForColumn, comparator: textSearch, value:null}, getData: (row) => (row.assignmentReason)}
        ]
    });

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
  
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }; 

    return (
      <>
          <Box sx={{width: "100%"}}>
            <DataTable
                id="SfdrAutoAssignedIndicatorsTable"
                data={data}
                setFilterCount={setDataCount}
                page={page}
                rowsPerPage={rowsPerPage}
                columnSettings={columnSettings}
                setColumnSettings={setColumnSettings}
                onRowClick={handleRowClick}
                tableContainerSx={{'& tbody tr': {cursor: 'pointer'}}}
                />         
            <br/>
            {
              dataCount > 5
              ?
                <CustomTablePagination
                    rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                    colSpan={3}
                    slotProps={{
                      select: {
                        'aria-label': 'rows per page',
                      },
                      actions: {
                        showFirstButton: true,
                        showLastButton: true,
                      },
                    }}
                    sx={{float: 'right'}}
                    count={dataCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    />                     
              :
              <></>              
            } 
          </Box>
      </>        
    );
  }
